// Types, constants, and helper functions for the image editor
import type { PercentCrop } from 'react-image-crop';

export interface AdjustmentState {
  brightness: number; // -100 to +100
  contrast: number;   // -100 to +100
  saturation: number; // -100 to +100
  temperature: number; // -100 to +100 (cool ↔ warm)
}

export interface CropState {
  percentCrop: PercentCrop | undefined; // % based crop — survives image resize
  zoom: number;
  rotation: number;
  flipH: boolean;
  flipV: boolean;
  aspect: number | undefined; // undefined = free
}

export interface EditorState {
  adjustments: AdjustmentState;
  crop: CropState;
  activeFilter: string; // filter preset key
}

export interface FilterPreset {
  key: string;
  labelKey: string; // i18n key
  adjustments: Partial<AdjustmentState>;
}

export const DEFAULT_ADJUSTMENTS: AdjustmentState = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
};

export const DEFAULT_CROP: CropState = {
  percentCrop: undefined,
  zoom: 1,
  rotation: 0,
  flipH: false,
  flipV: false,
  aspect: undefined,
};

export const ASPECT_OPTIONS = [
  { labelKey: 'imageEditor.free' as const, value: undefined },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
] as const;

export const FILTER_PRESETS: FilterPreset[] = [
  {
    key: 'original',
    labelKey: 'imageEditor.filterOriginal',
    adjustments: {},
  },
  {
    key: 'enhanced',
    labelKey: 'imageEditor.filterEnhanced',
    adjustments: { brightness: 10, contrast: 15, saturation: 10 },
  },
  {
    key: 'vivid',
    labelKey: 'imageEditor.filterVivid',
    adjustments: { saturation: 50, contrast: 30 },
  },
  {
    key: 'bw',
    labelKey: 'imageEditor.filterBW',
    adjustments: { saturation: -100 },
  },
  {
    key: 'vintage',
    labelKey: 'imageEditor.filterVintage',
    adjustments: { temperature: 30, saturation: -20, contrast: 10 },
  },
  {
    key: 'cool',
    labelKey: 'imageEditor.filterCool',
    adjustments: { temperature: -40, brightness: 5 },
  },
];

export type EditorTab = 'crop' | 'adjust' | 'filter';

export const HISTORY_LIMIT = 30;

/**
 * Convert adjustment values (-100..+100) to CSS filter values.
 * Brightness: 0 = 1.0 (100%), -100 = 0.0, +100 = 2.0
 * Contrast:   same mapping
 * Saturate:   same mapping
 * Temperature: implemented as a combination of sepia + hue-rotate
 */
export function buildCSSFilter(adjustments: AdjustmentState): string {
  const parts: string[] = [];

  // brightness: -100 → 0, 0 → 1, +100 → 2
  const b = 1 + adjustments.brightness / 100;
  if (b !== 1) parts.push(`brightness(${b.toFixed(3)})`);

  // contrast: -100 → 0, 0 → 1, +100 → 2
  const c = 1 + adjustments.contrast / 100;
  if (c !== 1) parts.push(`contrast(${c.toFixed(3)})`);

  // saturate: -100 → 0, 0 → 1, +100 → 2
  const s = 1 + adjustments.saturation / 100;
  if (s !== 1) parts.push(`saturate(${s.toFixed(3)})`);

  // Temperature: warm = sepia, cool = hue-rotate toward blue
  if (adjustments.temperature > 0) {
    // Warm: add sepia (max 40% at +100)
    const sepia = (adjustments.temperature / 100) * 0.4;
    parts.push(`sepia(${sepia.toFixed(3)})`);
  } else if (adjustments.temperature < 0) {
    // Cool: hue-rotate toward blue (max 30deg at -100)
    const hue = (adjustments.temperature / 100) * 30;
    parts.push(`hue-rotate(${hue.toFixed(1)}deg)`);
  }

  return parts.length > 0 ? parts.join(' ') : 'none';
}

/**
 * Merge a filter preset's adjustments on top of manual adjustments.
 * Preset values act as base; manual adjustments add on top.
 */
export function mergeAdjustments(
  preset: Partial<AdjustmentState>,
  manual: AdjustmentState
): AdjustmentState {
  return {
    brightness: clamp((preset.brightness ?? 0) + manual.brightness, -100, 100),
    contrast: clamp((preset.contrast ?? 0) + manual.contrast, -100, 100),
    saturation: clamp((preset.saturation ?? 0) + manual.saturation, -100, 100),
    temperature: clamp((preset.temperature ?? 0) + manual.temperature, -100, 100),
  };
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
